import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Percent, TrendingDown, TrendingUp, Minus } from "lucide-react";
import api from "../../lib/api";
import BentoSlotSkeleton from "./BentoSlotSkeleton";

// Compact food-cost KPI for the dashboard.
// Shows: current day %, 30d avg, best/worst, classification tone,
// and a 30-point sparkline built inline with SVG.
//
// Auto-hide rule:
//   • Hide automatically when there is no significant data yet
//     (no current %, no 30d average AND no sparkline points).
// Persistent dismiss removed (Feb 2026) — users hid this once and never
// got it back; now they can permanently hide via "Editar panel" instead.

const CLASSIFICATION_TONE = {
  excellent: { bg: "bg-emerald-50",  bd: "border-emerald-200", fg: "text-emerald-700", label: "Excelente" },
  good:      { bg: "bg-teal-50",     bd: "border-teal-200",    fg: "text-teal-700",    label: "Saludable" },
  warning:   { bg: "bg-amber-50",    bd: "border-amber-200",   fg: "text-amber-700",   label: "Vigilar" },
  critical:  { bg: "bg-rose-50",     bd: "border-rose-200",    fg: "text-rose-700",    label: "Crítico" },
  unknown:   { bg: "bg-muted/50",     bd: "border-border",    fg: "text-muted-foreground",    label: "Sin datos" },
};

export default function FoodCostKPI({ restaurantId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.getFoodCostSeries(restaurantId, 30);
      setData(r);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  if (!restaurantId) return null;
  // Reserve slot while loading so the bento grid doesn't reflow.
  if (loading || !data) return <BentoSlotSkeleton height={80} />;

  const current = data.current?.food_cost_pct;
  const avg = data.average_pct;

  // Sparkline points (computed early so we can use them in the auto-hide rule)
  const points = (data.days || []).filter(d => d.food_cost_pct !== null && d.food_cost_pct !== undefined);

  // ── Auto-hide when there is no significant data yet ───────────────────
  // Restaurant doesn't have TPV sales nor recipes with theoretical cost,
  // so the panel adds noise instead of insight.
  const hasNoSignificantData =
    (current === null || current === undefined) &&
    (avg === null || avg === undefined) &&
    points.length === 0;
  if (hasNoSignificantData) return null;

  const tone = CLASSIFICATION_TONE[data.classification] || CLASSIFICATION_TONE.unknown;
  const delta = (current !== null && current !== undefined && avg !== null && avg !== undefined)
    ? current - avg : null;
  const DeltaIcon = delta === null ? Minus : delta > 0.5 ? TrendingUp : delta < -0.5 ? TrendingDown : Minus;
  const deltaColor = delta === null ? "text-muted-foreground" : delta > 0.5 ? "text-rose-700" : delta < -0.5 ? "text-emerald-700" : "text-muted-foreground";

  // Sparkline path: normalize pct values over min-max
  let sparkPath = "";
  if (points.length > 1) {
    const values = points.map(p => p.food_cost_pct);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const stepX = 100 / (points.length - 1);
    sparkPath = points.map((p, i) => {
      const x = i * stepX;
      const y = 30 - ((p.food_cost_pct - min) / span) * 28 - 1;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(" ");
  }

  return (
    <Card
      data-testid="food-cost-kpi-card"
      className={`border ${tone.bd} cursor-pointer hover:brightness-[0.98] transition-colors relative`}
      onClick={() => navigate("/app/finance?tab=forecast")}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <Percent className="h-3 w-3" />
              Food-cost (CMV teórico)
            </div>
            <div className="flex items-baseline gap-2">
              <div className={`text-3xl font-bold ${tone.fg} tabular-nums`}>
                {current !== null && current !== undefined ? `${current.toFixed(1)}%` : "—"}
              </div>
              <div className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${tone.bg} ${tone.fg} border ${tone.bd}`}>
                {tone.label}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
              <span>Media 30d: <b className="text-foreground tabular-nums">{avg !== null ? `${avg.toFixed(1)}%` : "—"}</b></span>
              {delta !== null && (
                <span className={`inline-flex items-center gap-0.5 ${deltaColor} font-medium`}>
                  <DeltaIcon className="h-3 w-3" />
                  {delta > 0 ? "+" : ""}{delta.toFixed(1)}pp
                </span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Mejor día: <b className="tabular-nums">{data.best_day_pct !== null ? `${data.best_day_pct.toFixed(1)}%` : "—"}</b>
              {" · "}
              Peor día: <b className="tabular-nums">{data.worst_day_pct !== null ? `${data.worst_day_pct.toFixed(1)}%` : "—"}</b>
            </div>
          </div>
          {sparkPath && (
            <svg viewBox="0 0 100 30" className="w-24 h-10 shrink-0 mr-6" preserveAspectRatio="none">
              <path d={sparkPath} fill="none"
                stroke={data.classification === "critical" ? "#e11d48" : data.classification === "warning" ? "#d97706" : data.classification === "good" ? "#0d9488" : "#059669"}
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
